/* ---------------------------------------------------------------------------
   "Imported …" — what the import actually did.

   The modeller has a dialog of its own for this, and it is suppressed: the
   import runs in a frame nobody can see (src/modeler/importModel.ts), so its
   summary would be shown to nobody and would sit there unanswered. The content
   comes back as data instead, and this is where it is shown.

   WHICH HALF MATTERS DEPENDS ON WHAT WAS IMPORTED, and there are two kinds of
   import.

   A bundle carrying the source's own MESH makes the model a displayed one. The
   vendored app switches into a viewer for it and drops its parametric branches
   entirely rather than drawing alongside them — so the geometry on screen, and
   the component tree a defect is pinned to, are the source's triangles and
   nothing else. Its parameters are then only a description of geometry this app
   did not draw and cannot redraw, and the modeller's list of parameters it could
   not take from the export describes a model that is never built. Every one of
   those lines was advice about a discrepancy nobody can see, several of them
   ending "set it in Bridge Settings" — for a model nobody opens to edit. So for
   a mesh import they are not shown, and what is left is the receipt.

   A bundle with NO mesh imports as it always did: the model stays parametric and
   editable, and the approximations are really in the geometry it builds. There
   the second list is the one that matters, and it is shown at full length — an
   engineer told only that it worked will compare the two models later and have to
   guess which differences are real.

   Which kind it was comes off the re-read model index (`ModelSummary.imported`,
   migration 0016), not off the wording of the lines themselves.

   Rendered into src/main.ts's dialog layer, like the delete confirmation next to
   it.
   --------------------------------------------------------------------------- */

import { h, icon, ICONS } from '../core/dom.ts';
import { actions, type ModelImportResult, type State } from '../core/store.ts';
import { button, scrim } from '../components/widgets.ts';
import { findBridge } from '../data/derive.ts';

/**
 * Whether the import left a model that is displayed rather than built.
 *
 * Looked up in the index the store re-reads before this opens — see
 * finishModelImport. False when the model cannot be found or the index has no
 * answer for it, which keeps the warnings rather than hiding them: of the two
 * ways to be wrong, showing an engineer an approximation that does not apply is
 * recoverable and hiding one that does is not.
 */
function displayedOnly(s: Readonly<State>, r: ModelImportResult): boolean {
  return s.models.models.find((m) => m.id === r.modelId)?.imported === true;
}

/** One bulleted line of the modeller's own wording, kept verbatim. */
function line(text: string, kind: 'applied' | 'warning'): HTMLElement {
  return h(
    'li',
    { class: `mimp__line mimp__line--${kind}` },
    h('span', { class: 'mimp__bullet', 'aria-hidden': 'true', text: kind === 'applied' ? '✓' : '!' }),
    h('span', { text }),
  );
}

/** The failure case: nothing was changed, and the reason is the whole content. */
function failure(r: ModelImportResult): HTMLElement {
  return h(
    'div',
    { class: 'dlg__note dlg__note--error' },
    icon(ICONS.import, { size: 13, stroke: 'currentColor' }),
    h('span', {
      text:
        `${r.error ?? 'The export could not be imported.'} ` +
        `"${r.modelName}" is unchanged — nothing was saved.`,
    }),
  );
}

/** The summary of the last import, or null when there is none to show. */
export function modelImportDialog(s: Readonly<State>): HTMLElement | null {
  const r = s.modelImportResult;
  if (!r) return null;

  const b = findBridge(r.bridgeId);
  /* No record to hang this on, which happens only where the register is
     empty. Closed rather than drawn over nothing — the same answer this
     dialog already gives when the thing it was opened for has gone. */
  if (b === null) return null;
  const close = (): void => actions.closeModelImportResult();
  const displayed = r.ok && displayedOnly(s, r);

  return h(
    'div',
    { class: 'dlg', ...scrim(close) },
    h(
      'div',
      {
        class: 'dlg__panel dlg__panel--sm',
        role: 'dialog',
        'aria-modal': 'true',
        'aria-label': r.ok ? 'Bridge model imported' : 'Import failed',
      },

      h(
        'div',
        { class: 'dlg__head' },
        h(
          'div',
          {},
          h('div', { class: 'dlg__kicker', text: `${b.id} · ${b.name}` }),
          h('h2', {
            class: 'dlg__title',
            // The imported bridge's own name, which is how the engineer will
            // recognise the file they picked — the model it went into is on the
            // line below, and they are rarely called the same thing.
            text: r.ok ? `Imported “${r.name}”` : 'Nothing was imported',
          }),
        ),
        h('span', { class: 'spacer' }),
        h(
          'button',
          {
            type: 'button',
            class: 'dlg__close',
            title: 'Close (Esc)',
            'aria-label': 'Close',
            onclick: close,
          },
          '×',
        ),
      ),

      r.ok
        ? h(
            'div',
            { class: 'dlg__note' },
            icon(ICONS.cube, { size: 13, stroke: 'var(--txt3)' }),
            h('span', {
              /* "Rebuilt" is the wrong word for a displayed model, and the
                 difference is the whole point of the distinction: this app did not
                 rebuild anything, it took the source's geometry and shows it. */
              text: [
                displayed
                  ? `"${r.modelName}" now shows the geometry from the export.`
                  : `"${r.modelName}" was rebuilt from the export.`,
                r.saveUnconfirmed
                  ? 'The save was sent to the Cloud but not confirmed before this closed — ' +
                    'reopen the record to check it.'
                  : 'It is saved to the Cloud.',
              ].join(' '),
            }),
          )
        : failure(r),

      /* THE ONE THING A DISPLAYED MODEL CANNOT SHRUG OFF. The modeller keeps an
         imported mesh in IndexedDB, so it exists only on the device that imported
         it; the .glb in R2 is the copy every other device draws from. Without it
         the record comes up in viewer mode with no mesh — a bridge missing its
         abutments and columns — and nothing would have said why. */
      r.ok && r.geometryError
        ? h(
            'div',
            { class: 'dlg__note dlg__note--error' },
            icon(ICONS.cube, { size: 13, stroke: 'currentColor' }),
            h('span', {
              text:
                `The model is saved, but its geometry could not be stored — ${r.geometryError} ` +
                `It will show on this device and not on others until it is re-exported ` +
                `from the record.`,
            }),
          )
        : null,

      r.applied.length > 0
        ? h(
            'div',
            { class: 'mimp__group' },
            h('div', { class: 'mimp__group-title', text: 'Imported' }),
            h('ul', { class: 'mimp__list' }, ...r.applied.map((t) => line(t, 'applied'))),
          )
        : null,

      /* What the import wrote into the inventory record — see fillRecordFromModel. */
      r.inventory?.error
        ? h(
            'div',
            { class: 'dlg__note dlg__note--error' },
            h('span', { text: `The inventory record was not filled from the model: ${r.inventory.error}` }),
          )
        : null,
      r.inventory && r.inventory.filled.length > 0
        ? h(
            'div',
            { class: 'mimp__group' },
            h('div', { class: 'mimp__group-title', text: 'Filled on the inventory record' }),
            h('ul', { class: 'mimp__list' }, ...r.inventory.filled.map((t) => line(t, 'applied'))),
          )
        : null,
      r.inventory && r.inventory.differs.length > 0
        ? h(
            'div',
            { class: 'mimp__group' },
            h('div', {
              class: 'mimp__group-title mimp__group-title--warn',
              text: 'Kept on the record — Fill from model in Edit record takes the model’s',
            }),
            h('ul', { class: 'mimp__list' }, ...r.inventory.differs.map((t) => line(t, 'warning'))),
          )
        : null,

      /* Only where the model it went into is still parametric — see the note at
         the top of this file. */
      r.warnings.length > 0 && !displayed
        ? h(
            'div',
            { class: 'mimp__group' },
            h('div', {
              class: 'mimp__group-title mimp__group-title--warn',
              text: 'Not imported, or approximated',
            }),
            h('ul', { class: 'mimp__list' }, ...r.warnings.map((t) => line(t, 'warning'))),
          )
        : null,

      h(
        'div',
        { class: 'dlg__foot' },
        h('span', {
          class: 'dlg__foot-note',
          /* Three notes, and the first two are different claims. A displayed model
             is on the record exactly as the source drew it, so there is nothing to
             check it against and nothing to correct — the drawing and the model
             are the same act. A parametric one was rebuilt from the export by this
             app, which is when comparing them is worth doing. */
          text: !r.ok
            ? 'Pick a Components bundle exported by CSiBridge — an alignment file may go with it.'
            : displayed
              ? 'The record shows this model as the source drew it. It is a view, not a rebuild — ' +
                'nothing here was regenerated from parameters.'
              : 'The record shows the imported model, rebuilt from the export. Worth checking ' +
                'against the drawing.',
        }),
        h('span', { class: 'spacer' }),
        button('Close', { primary: true, onClick: close }),
      ),
    ),
  );
}
